/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import { hooks } from '@broadleaf/admin-components';
import PropTypes from 'prop-types';
import { get, isEmpty, isNil } from 'lodash';

import { ActionPropShape } from 'app/common/constants/CommonProps';
import SecondaryAction from 'app/view/components/actions/SecondaryAction';
import useImpersonateUser from 'app/view/components/hooks/useImpersonateUser';
import { LocalStateShape } from 'app/view/components/prop-types';
import {
  isDealerNetworkResolverType,
  isDefaultResolverType,
  isWhiteLabelResolverType
} from 'app/view/components/actions';

const { useCurrentApplication } = hooks;
const ImpersonateCustom = ({
  actionDefinition,
  className,
  state,
  disabled = false
}) => {
  const application = useCurrentApplication();
  const urlResolverType = get(actionDefinition, 'attributes.urlResolverType');
  const isOrderView = !isEmpty(get(state, 'data.orderItems'));
  const isImpersonateGuest =
    actionDefinition.type === 'IMPERSONATE_GUEST_CUSTOM';
  const isRegisteredCustomer = get(state, 'data.userRegistered', false);

  const impersonateUser = useImpersonateUser(
    get(state, 'data.username', undefined),
    urlResolverType
  );

  const shouldRender = shouldRenderImpersonate(
    urlResolverType,
    application,
    isOrderView,
    isImpersonateGuest,
    isRegisteredCustomer
  );

  if (!shouldRender) {
    return null;
  }

  return (
    <SecondaryAction
      className={className}
      disabled={disabled}
      onClick={impersonateUser}
      title={actionDefinition.label}
    >
      {actionDefinition.label}
    </SecondaryAction>
  );
};

function shouldRenderImpersonate(
  urlResolverType,
  application,
  isOrderView,
  isImpersonateGuestType,
  isRegisteredCustomer
) {
  // No app
  if (isNil(application)) {
    return false;
  }
  if (isOrderView && !isImpersonateGuestType && !isRegisteredCustomer) {
    // If order view and non-registered customer, don't show impersonate
    return false;
  }
  if (isImpersonateGuestType && isRegisteredCustomer) {
    // If impersonate guest type and registered customer, don't show impersonate
    return false;
  }
  const dealerNetwork = isAppDealerNetwork(application);
  if (isDefaultResolverType(urlResolverType) && !dealerNetwork) {
    // Don't render default type for dealer network.
    return true;
  }
  if (isWhiteLabelResolverType(urlResolverType) && !dealerNetwork) {
    // Don't render white label type for dealer network or if app doesn't have whitelabel.
    return canImpersonateWhitelabel(application);
  }
  if (isDealerNetworkResolverType(urlResolverType)) {
    // Don't render dealer network type if app is not dn member or not active on dn.
    return canImpersonateDealerNetwork(application);
  }
  return false;
}

function canImpersonateWhitelabel(application) {
  const domainType = get(application, 'domainType');
  return !isNil(domainType);
}

function canImpersonateDealerNetwork(application) {
  const shopAsDealerNetwork = get(
    application,
    'attributes.shopAsDealerNetwork',
    false
  );
  const isActive = !get(application, 'deactivated', true);
  return isActive && (isAppDealerNetwork(application) || shopAsDealerNetwork);
}

function isAppDealerNetwork(application) {
  return get(application, 'dealerNetwork', false);
}

ImpersonateCustom.propTypes = {
  actionDefinition: ActionPropShape.isRequired,
  state: LocalStateShape.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool
};

export default ImpersonateCustom;
